import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Spinner,
  Input,
  Select,
  Field,
  MessageBar,
  MessageBarBody,
  Link,
  Switch,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  PlayFilled,
  DismissRegular,
  ErrorCircleRegular,
  DeleteRegular,
  AddRegular,
} from '@fluentui/react-icons';
import { CopyIconButton } from '@/components/shared';
import { useWorkflowTriggers, useTriggerWorkflow } from '@/hooks/useMonitoringData';
import { type TriggerInfo, type TriggerType, canRunBeInvokedWithPayload } from '@/services/monitoringApi';
import { JsonEditor } from '../JsonEditor';
import { ConnectionsDialog } from '../ConnectionsDialog';
import { useTelemetry } from '@/hooks/useTelemetry';
import { isTestDraftGateBlocked, useRevisions, applicationKeys, getLatestRevision } from '@/hooks/useApplicationManagement';
import { useQueryClient } from '@tanstack/react-query';
import type { Revision } from '@/types/automation';
import { useValidationStore } from '@/stores/validationStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { canProvideTestData } from '@/services/testDataService';
import { useTestDataStore } from '@/stores/testDataStore';
import { useParams } from 'react-router-dom';
import { useConnectorSwagger } from '@/hooks/useConnectorSwagger';
import { useTriggerSchemaSample } from '@/hooks/useTriggerSchemaSample';
import { useConnectionStore } from '@/stores/connectionStore';
import { getConnectorIdForAction } from '@/utils/connections';
import { useWorkflowStore } from '@/stores/workflowStore';
import { flattenValidationErrors } from '@/utils/validation';
import { walkAllActions } from '@/utils/workflow/walkActions';
import { doesOperationRequireConnection, extractReferenceKeyFromOperation } from '@/services/connectionService';
import { useTriggerWorkflowDialogStyles } from './TriggerWorkflowDialog.styles';
import {
  EMPTY_JSON_EDITOR_VALUE,
  getSubmitButtonLabel,
  getTriggerTypeLabel,
  hasEnabledMocks,
  hasResolvedConnection,
  isActionMockActive,
  resolveTestDataSeed,
  runTriggerFlow,
  shouldShowDraftPayloadEditor,
} from './triggerDialogLogic';
import type { RunTriggerPhase, TriggerMockEntry, TriggerWorkflowDialogProps } from './types';


/** Number of validation errors shown collapsed before the "Show all" toggle. */
const VALIDATION_ERROR_PREVIEW_LIMIT = 5;

export const TriggerWorkflowDialog = ({
  open,
  onClose,
  onSuccess,
  workflowTriggers,
  onSaveDraft,
  onMockRun,
  onStreamingDraftTrigger,
}: TriggerWorkflowDialogProps) => {
  const styles = useTriggerWorkflowDialogStyles();
  const { data: apiTriggers, isLoading: isLoadingTriggers } = useWorkflowTriggers();
  const triggerMutation = useTriggerWorkflow();
  const queryClient = useQueryClient();
  const { trackEvent } = useTelemetry();
  const { workflowName, projectName, appName } = useParams<{
    workflowName: string;
    projectName: string;
    appName: string;
  }>();
  const isDraftMode = !!onSaveDraft;
  // Gate Test Draft on the app's newest revision being the one serving traffic.
  // Saving a connection secret or app setting stores it as a container
  // environment variable, which ACA applies by rolling a new revision, so until
  // the cutover completes the old container answers the run without the new
  // setting. Opening this dialog is what checks that, since the query below only
  // runs while it is open.
  const {
    data: revisions,
    isFetching: isRevisionsFetching,
    isPending: isRevisionsPending,
  } = useRevisions(projectName ?? '', appName ?? '', {
    enabled: open && isDraftMode && !!projectName && !!appName,
  });
  const isPreparingRevision =
    isDraftMode && isTestDraftGateBlocked(revisions, isRevisionsFetching, isRevisionsPending);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isConnectionsOpen, setIsConnectionsOpen] = useState(false);
  const hasValidated = useValidationStore((s) => s.hasValidated);
  const nodeErrors = useValidationStore((s) => s.nodeErrors);
  const globalErrors = useValidationStore((s) => s.globalErrors);
  const validationErrors = useMemo(
    () => flattenValidationErrors(nodeErrors, globalErrors),
    [nodeErrors, globalErrors],
  );
  const validationErrorCount = validationErrors.length;
  const [showAllValidationErrors, setShowAllValidationErrors] = useState(false);
  const addNotification = useNotificationStore((s) => s.addNotification);

  // Global mock toggle — read live from the workflow store so the dialog can
  // flip between a live draft run and a mock run.
  const mockedNodes = useWorkflowStore((s) => s.mockedNodes);
  const mockRunEnabledFromStore = useWorkflowStore((s) => s.mockRunEnabled);
  const setMockRunEnabledInStore = useWorkflowStore((s) => s.setMockRunEnabled);
  // True when at least one mocked node has its mock active (has mock data and enabled).
  const hasMocks = useMemo(() => hasEnabledMocks(mockedNodes), [mockedNodes]);

  const [mockRunEnabled, setMockRunEnabled] = useState<boolean>(mockRunEnabledFromStore !== undefined ? mockRunEnabledFromStore : hasMocks);

  useEffect(() => {
    if (mockRunEnabledFromStore !== undefined || hasMocks !== undefined) {
      setMockRunEnabled(mockRunEnabledFromStore !== undefined ? mockRunEnabledFromStore : hasMocks);
    }
  }, [mockRunEnabledFromStore, hasMocks]);

  const useMockRun = useMemo(() => isDraftMode && hasMocks && mockRunEnabled, [isDraftMode, hasMocks, mockRunEnabled]);

  // Detect operations whose connection is missing. A node is valid when its
  // mock will replace the live call (mock toggle on AND its mock entry is
  // active), or when it has a resolved connection. Triggers and actions that
  // don't require a connection (Compose, If, Foreach, Http, …) are skipped.
  const workflowDefinition = useWorkflowStore((s) => s.workflow);
  const connectionMapping = useConnectionStore((s) => s.mapping);
  const connectionReferences = useConnectionStore((state) => state.references);
  const invalidConnectionNodeIds = useMemo<string[]>(() => {
    const triggers = workflowDefinition?.definition?.triggers;
    const actions = workflowDefinition?.definition?.actions;
    if (!triggers && !actions) return [];

    const isMocked = (nodeId: string): boolean => {
      if (!useMockRun) return false;
      const entry = mockedNodes[nodeId];
      if (!entry) return false;
      // Trigger payload entries (no `actionName`) always count as mocked —
      // the trigger payload is always sent with a mock run.
      if (typeof entry === 'object' && entry !== null && !('actionName' in entry)) {
        return true;
      }
      return isActionMockActive(entry);
    };

    const ids: string[] = [];

    if (triggers) {
      for (const [name, trigger] of Object.entries(triggers)) {
        const type = (trigger as { type?: string })?.type ?? '';
        if (!doesOperationRequireConnection(type)) continue;
        const nodeId = `trigger-${name}`;
        if (isMocked(nodeId)) continue;
        const fallbackRefKey = extractReferenceKeyFromOperation(trigger as Record<string, unknown>);
        if (!hasResolvedConnection(nodeId, connectionMapping, connectionReferences, fallbackRefKey)) {
          ids.push(nodeId);
        }
      }
    }

    if (actions) {
      walkAllActions(actions as Record<string, unknown>, (_names, action, _scopedName, nodeId) => {
        const type = (action as { type?: string })?.type ?? '';
        if (!doesOperationRequireConnection(type)) return;
        if (isMocked(nodeId)) return;
        const fallbackRefKey = extractReferenceKeyFromOperation(action as Record<string, unknown>);
        if (!hasResolvedConnection(nodeId, connectionMapping, connectionReferences, fallbackRefKey)) {
          ids.push(nodeId);
        }
      });
    }

    return ids;
  }, [workflowDefinition, mockedNodes, useMockRun, connectionMapping, connectionReferences]);

  const handleMockToggleChange = useCallback((_ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    setMockRunEnabled(data.checked);
    setMockRunEnabledInStore(data.checked);
  }, [setMockRunEnabledInStore]);
  
  // Build trigger list. In draft mode, the workflow definition is the source of truth
  // for which triggers exist (the canvas). The runtime API may have stale data from
  // the last published version. We use workflowTriggers as primary and enrich from
  // apiTriggers for runtime-only info (callback URL, method).
  const triggers = useMemo((): TriggerInfo[] | undefined => {
    // Primary: workflow definition triggers (what's actually on the canvas)
    if (workflowTriggers && Object.keys(workflowTriggers).length > 0) {
      const apiTriggerMap = new Map(
        (apiTriggers ?? []).map(t => [t.name, t])
      );
      return Object.entries(workflowTriggers).map(([name, trigger]): TriggerInfo => {
        const triggerType = trigger.type;
        const triggerKind = trigger.kind;
        const acceptsPayload = ['Request', 'HttpWebhook', 'Manual'].includes(triggerType);
        const apiTrigger = apiTriggerMap.get(name);
        return {
          name,
          type: triggerType as TriggerType,
          acceptsPayload,
          ...(triggerKind ? { kind: triggerKind } : {}),
          // Enrich with runtime data when available (callback URL, method)
          ...(apiTrigger?.callbackUrl ? { callbackUrl: apiTrigger.callbackUrl } : {}),
          ...(apiTrigger?.method ? { method: apiTrigger.method } : {}),
        };
      });
    }

    // Fallback: use API triggers when no definition is available
    if (apiTriggers && apiTriggers.length > 0) {
      return apiTriggers;
    }

    if (!apiTriggers) return undefined;
    return apiTriggers;
  }, [apiTriggers, workflowTriggers]);

  // Basic state
  const [selectedTriggerName, setSelectedTriggerName] = useState<string>('');
  const [triggerError, setTriggerError] = useState<string>('');

   // Payload state — draft mode uses testData JSON, published mode uses full payload fields
  const [testData, setTestData] = useState<string>('{\n  \n}');
  const [testDataError, setTestDataError] = useState<string>('');
  const getPayload = useTestDataStore((s) => s.getPayload);
  const setPayloadToStore = useTestDataStore((s) => s.setPayload);
  const currentSchemaHash = useRef<string>('0');
  const userHasEdited = useRef(false);
  const lastAutoFilledTrigger = useRef<string>('');

  // Published mode payload state (full customization for Request triggers)
  const [body, setBody] = useState<string>('{\n  \n}');
  const [bodyError, setBodyError] = useState<string>('');
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([]);
  const [queries, setQueries] = useState<{ key: string; value: string }[]>([]);
  // Get the selected trigger info
  const selectedTrigger = useMemo((): TriggerInfo | undefined => {
    if (!triggers || !selectedTriggerName) return undefined;
    return triggers.find((t) => t.name === selectedTriggerName);
  }, [triggers, selectedTriggerName]);

  // Extract connectorId for swagger schema pre-fill (ApiConnection triggers only)
  const selectedTriggerConnectorId = useMemo(() => {
    if (!selectedTrigger || !canProvideTestData(selectedTrigger.type)) return null;
    const triggerDef = workflowTriggers?.[selectedTrigger.name];
    return triggerDef ? getConnectorIdForAction(triggerDef, connectionReferences) : null;
  }, [selectedTrigger, workflowTriggers, connectionReferences]);

  const { data: swaggerResult, isLoading: isSwaggerLoading } = useConnectorSwagger(selectedTriggerConnectorId);

  // Resolve the swagger operationId for the selected trigger. We need this
  // outside the effect so the schema-sample hook (below) can run as soon as
  // the swagger is available, in parallel with the heuristic sample generator
  // path inside the effect.
  const selectedTriggerOperationId = useMemo<string | null>(() => {
    if (!selectedTrigger || !swaggerResult) return null;
    const triggerDef = workflowTriggers?.[selectedTrigger.name];
    const inputs = triggerDef?.inputs;
    const host = inputs?.host as Record<string, unknown> | undefined;
    if (typeof host?.operationId === 'string') {
      return host.operationId;
    }
    if (typeof inputs?.path === 'string' && swaggerResult.parser) {
      const triggerMethod = (inputs?.method as string)?.toLowerCase() ?? 'get';
      return swaggerResult.parser.resolveOperationIdFromFetchPath(inputs.path as string, triggerMethod) ?? null;
    }
    return null;
  }, [selectedTrigger, swaggerResult, workflowTriggers]);

  // Pre-generated trigger output sample from Otto's `schemaSamples` catalog.
  // Returns null when no sample exists for this connector/operation — we fall
  // back to `generateSampleFromSchema` (heuristic) below in that case.
  const { data: schemaSample } = useTriggerSchemaSample({
    connectorId: selectedTriggerConnectorId,
    operationId: selectedTriggerOperationId,
    triggerType: selectedTrigger?.type,
  });

  // Auto-select first trigger when triggers load, or reset if selection is stale
  useEffect(() => {
    if (triggers && triggers.length > 0) {
      const selectionValid = triggers.some(t => t.name === selectedTriggerName);
      if (!selectedTriggerName || !selectionValid) {
        const firstTrigger = triggers[0];
        if (firstTrigger) {
          setSelectedTriggerName(firstTrigger.name);
        }
      }
    }
  }, [triggers, selectedTriggerName]);

  // Load persisted test data when trigger changes (all triggers, not just ApiConnection)
  useEffect(() => {
    if (!selectedTrigger || !workflowName) return;

    // A trigger the user has already typed into keeps their edit.
    if (userHasEdited.current && lastAutoFilledTrigger.current === selectedTrigger.name) {
      return;
    }

    if (lastAutoFilledTrigger.current !== selectedTrigger.name) {
      userHasEdited.current = false;
      lastAutoFilledTrigger.current = selectedTrigger.name;
    }

    const seed = resolveTestDataSeed({
      triggerType: selectedTrigger.type,
      mockRunEnabled,
      triggerMock: mockedNodes[`trigger-${selectedTrigger.name}`] as TriggerMockEntry | undefined,
      storedPayload: getPayload(workflowName, selectedTrigger.name),
      isSwaggerLoading,
      schemaSample,
      swaggerPaths: swaggerResult?.swagger?.paths as Record<string, unknown> | undefined,
      swaggerDefinitions: swaggerResult?.swagger?.definitions as Record<string, unknown> | undefined,
      operationId: selectedTriggerOperationId ?? undefined,
      requestSchema: workflowTriggers?.[selectedTrigger.name]?.inputs?.schema as Record<string, unknown> | undefined,
    });

    currentSchemaHash.current = seed.schemaHash;
    setTestData(seed.testData);
    setTestDataError('');
  }, [selectedTrigger, workflowName, getPayload, swaggerResult, isSwaggerLoading, workflowTriggers, schemaSample, selectedTriggerOperationId, open, mockedNodes, mockRunEnabled]);

  const resetState = useCallback(() => {
    setTriggerError('');
    setIsSavingDraft(false);
    setTestData(EMPTY_JSON_EDITOR_VALUE);
    setTestDataError('');
    setBody(EMPTY_JSON_EDITOR_VALUE);
    setBodyError('');
    setHeaders([]);
    setQueries([]);
    userHasEdited.current = false;
    lastAutoFilledTrigger.current = '';
  }, []);

  const completeRunTrigger = useCallback((runId?: string) => {
    onSuccess?.(runId);
    onClose();
    resetState();
    setIsRunningTrigger(false);
  }, [onSuccess, onClose, resetState]);

  const [isRunningTrigger, setIsRunningTrigger] = useState(false);
  const setRunTriggerPhase = useCallback((phase: RunTriggerPhase) => {
    setIsSavingDraft(phase === 'saving-draft');
    setIsRunningTrigger(phase === 'running');
  }, []);

  const handleTrigger = useCallback(async () => {
    if (!selectedTrigger) return;

    setTriggerError('');

    const result = await runTriggerFlow({
      selectedTrigger,
      workflowName,
      isMockRunActive: useMockRun,
      headers,
      queries,
      body,
      testData,
    }, {
      saveDraft: onSaveDraft,
      mockRun: onMockRun,
      streamingTrigger: onStreamingDraftTrigger,
      submitTrigger: triggerMutation.mutateAsync,
      readLatestRevision: () => {
        // Read fresh revision data from the query cache (not the render closure)
        // because the draft save above may have triggered a new revision.
        const freshRevisions = projectName && appName
          ? queryClient.getQueryData<Revision[]>(applicationKeys.revisions(projectName, appName))
          : undefined;
        return getLatestRevision(freshRevisions);
      },
      sleep: (ms) => new Promise(resolve => setTimeout(resolve, ms)),
      setPhase: setRunTriggerPhase,
      trackEvent,
      persistTestData: () => {
        if (!workflowName) return;
        setPayloadToStore(workflowName, selectedTrigger.name, {
          payload: testData,
          schemaHash: currentSchemaHash.current,
          lastUsed: Date.now(),
        });
      },
    });

    switch (result.kind) {
      case 'rejected':
      case 'error':
        setTriggerError(result.message);
        return;
      case 'invalid-payload':
        if (result.field === 'body') setBodyError(result.message);
        else setTestDataError(result.message);
        return;
      case 'success':
        if (result.notify) {
          addNotification({
            type: 'success',
            title: 'Workflow run started',
            // Status-neutral: the run may have completed with a non-2xx Response
            // (e.g. a 400 branch). Success here means the run was created, not that
            // the workflow's own response was 2xx — see issue #2379. Kept caller-
            // agnostic because some callers refresh a runs list rather than navigate.
            message: 'The workflow run was created',
            durationMs: 3000,
          });
        }
        return completeRunTrigger(result.runId);
    }
  }, [
    selectedTrigger,
    workflowName,
    useMockRun,
    headers,
    queries,
    body,
    testData,
    onSaveDraft,
    onMockRun,
    onStreamingDraftTrigger,
    triggerMutation.mutateAsync,
    projectName,
    appName,
    queryClient,
    setRunTriggerPhase,
    trackEvent,
    setPayloadToStore,
    addNotification,
    completeRunTrigger,
  ]);

  const handleClose = useCallback(() => {
    // Don't run the full resetState here: clearing testData / body / headers
    // / queries during the Fluent close animation causes the Monaco editor
    // (and the rest of the dialog content) to reflow mid-fade, which the
    // user sees as a jitter. Only clear ephemeral UI state — the auto-fill
    // effect re-runs on reopen (its `open` dep) and re-derives the editor
    // contents from persisted edit / curated catalog / heuristic.
    setTriggerError('');
    setIsSavingDraft(false);
    onClose();
  }, [onClose]);

  const canTrigger = useMemo(() => {
    if (!selectedTrigger) return false;
    if (testDataError || bodyError) return false;
    return true;
  }, [selectedTrigger, testDataError, bodyError]);

  return (
    <>
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogTitle>
          {useMockRun ? 'Test draft workflow' : onSaveDraft ? 'Test draft workflow' : 'Run workflow'}
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={handleClose}
            className={styles.dialogCloseButton}
            aria-label="Close"
          />
        </DialogTitle>
        <DialogBody>
          <DialogContent className={styles.content}>
            {isDraftMode && hasMocks && !!onMockRun && (
              <div className={styles.mockToggleRow}>
                <Switch
                  checked={mockRunEnabled}
                  onChange={handleMockToggleChange}
                  label={mockRunEnabled ? 'Run with test data' : 'Run draft live'}
                />
                <Text className={styles.mockToggleLabel}>
                  When on, actions with test data return their configured responses
                  instead of running live.
                </Text>
              </div>
            )}
            {isLoadingTriggers ? (
              <div className={styles.loadingTriggers}>
                <Spinner size="small" label="Loading triggers..." />
              </div>
            ) : !triggers || triggers.length === 0 ? (
              <MessageBar intent="warning">
                <MessageBarBody>No triggers found for this workflow.</MessageBarBody>
              </MessageBar>
            ) : (
              <>
                {/* Trigger Selection */}
                <Field label="Trigger">
                  <Select
                    className={styles.triggerSelect}
                    value={selectedTriggerName}
                    onChange={(_, data) => setSelectedTriggerName(data.value)}
                  >
                    {triggers.map((trigger) => (
                      <option key={trigger.name} value={trigger.name}>
                        {trigger.name} ({getTriggerTypeLabel(trigger.type)})
                      </option>
                    ))}
                  </Select>
                </Field>

                {/* Trigger Info */}
                {selectedTrigger && (
                  <div className={styles.triggerInfo}>
                    <div className={styles.triggerInfoRow}>
                      <Text className={styles.triggerInfoLabel}>Type:</Text>
                      <Text className={styles.triggerInfoValue}>
                        {getTriggerTypeLabel(selectedTrigger.type)}
                      </Text>
                    </div>
                    {selectedTrigger.callbackUrl && (
                      <div className={styles.triggerInfoRow}>
                        <Text className={styles.triggerInfoLabel}>URL:</Text>
                        <Tooltip content={selectedTrigger.callbackUrl} relationship="label" withArrow>
                          <Text
                            className={styles.triggerInfoUrlValue}
                            title={selectedTrigger.callbackUrl}
                          >
                            {selectedTrigger.callbackUrl}
                          </Text>
                        </Tooltip>
                        <CopyIconButton value={selectedTrigger.callbackUrl} label="URL" size="small" />
                      </div>
                    )}
                    {selectedTrigger.method && (
                      <div className={styles.triggerInfoRow}>
                        <Text className={styles.triggerInfoLabel}>Method:</Text>
                        <Text className={styles.triggerInfoValue}>{selectedTrigger.method}</Text>
                      </div>
                    )}
                  </div>
                )}

                {/* Payload Editor — full customization for published Request triggers, test data for draft */}
                {selectedTrigger && (
                  <div className={styles.payloadSection}>
                    {onSaveDraft ? (
                      // Draft mode: payload editor for triggers that accept input,
                      // or a short note for Recurrence (fires the trigger once).
                      shouldShowDraftPayloadEditor(selectedTrigger.type) ? (
                        <>
                          {canProvideTestData(selectedTrigger.type) && (
                            <MessageBar intent="info" layout="multiline">
                              <MessageBarBody>
                                Provide sample data that simulates what the trigger would return.
                              </MessageBarBody>
                            </MessageBar>
                          )}
                          <MessageBar intent="info" layout="multiline" data-testid="trigger-test-data-notice">
                            <MessageBarBody>
                              Editing this JSON does not update the trigger's Test data tab.
                              {' '}Edit action test outputs in each action's Test data tab.
                            </MessageBarBody>
                          </MessageBar>
                          <Field
                            label="Test data (JSON)"
                            hint="Enter the JSON payload for this test run — this will flow through your workflow actions."
                            validationState={testDataError ? 'error' : 'none'}
                            validationMessage={testDataError || null}
                          >
                            <JsonEditor
                              className={styles.jsonEditor}
                              value={testData}
                              onChange={(value: string) => {
                                setTestData(value);
                                userHasEdited.current = true;
                                if (value.trim()) {
                                  try { JSON.parse(value); setTestDataError(''); } catch { setTestDataError('Invalid JSON'); }
                                } else {
                                  setTestDataError('');
                                }
                              }}
                              hasError={!!testDataError}
                              minHeight={150}
                              maxHeight={300}
                            />
                          </Field>
                        </>
                      ) : (
                        <MessageBar intent="info" layout="multiline">
                          <MessageBarBody>
                            Recurrence triggers run on a schedule once the workflow is published.
                            Click Test Draft to run the workflow once now.
                          </MessageBarBody>
                        </MessageBar>
                      )
                    ) : canRunBeInvokedWithPayload(selectedTrigger.type, selectedTrigger.kind) ? (
                      // Published Request trigger: full payload customization
                      <>
                        {/* Headers */}
                        <Field label="Headers">
                          <div className={styles.keyValueSection}>
                            {headers.map((pair, i) => (
                              <div key={i} className={styles.keyValueRow}>
                                <Input
                                  className={styles.keyInput}
                                  placeholder="Header name"
                                  value={pair.key}
                                  onChange={(_, d) => {
                                    const next = [...headers];
                                    next[i] = { ...pair, key: d.value };
                                    setHeaders(next);
                                  }}
                                />
                                <Input
                                  className={styles.valueInput}
                                  placeholder="Value"
                                  value={pair.value}
                                  onChange={(_, d) => {
                                    const next = [...headers];
                                    next[i] = { ...pair, value: d.value };
                                    setHeaders(next);
                                  }}
                                />
                                <Button
                                  className={styles.deleteButton}
                                  appearance="subtle"
                                  icon={<DeleteRegular />}
                                  onClick={() => setHeaders(headers.filter((_, j) => j !== i))}
                                  aria-label="Remove header"
                                />
                              </div>
                            ))}
                            <Button
                              className={styles.addButton}
                              appearance="subtle"
                              icon={<AddRegular />}
                              onClick={() => setHeaders([...headers, { key: '', value: '' }])}
                            >
                              Add header
                            </Button>
                          </div>
                        </Field>

                        {/* Query Parameters */}
                        <Field label="Query parameters">
                          <div className={styles.keyValueSection}>
                            {queries.map((pair, i) => (
                              <div key={i} className={styles.keyValueRow}>
                                <Input
                                  className={styles.keyInput}
                                  placeholder="Parameter name"
                                  value={pair.key}
                                  onChange={(_, d) => {
                                    const next = [...queries];
                                    next[i] = { ...pair, key: d.value };
                                    setQueries(next);
                                  }}
                                />
                                <Input
                                  className={styles.valueInput}
                                  placeholder="Value"
                                  value={pair.value}
                                  onChange={(_, d) => {
                                    const next = [...queries];
                                    next[i] = { ...pair, value: d.value };
                                    setQueries(next);
                                  }}
                                />
                                <Button
                                  className={styles.deleteButton}
                                  appearance="subtle"
                                  icon={<DeleteRegular />}
                                  onClick={() => setQueries(queries.filter((_, j) => j !== i))}
                                  aria-label="Remove parameter"
                                />
                              </div>
                            ))}
                            <Button
                              className={styles.addButton}
                              appearance="subtle"
                              icon={<AddRegular />}
                              onClick={() => setQueries([...queries, { key: '', value: '' }])}
                            >
                              Add parameter
                            </Button>
                          </div>
                        </Field>

                        {/* Body */}
                        <Field
                          label="Body"
                          hint="JSON body to send with the request."
                          validationState={bodyError ? 'error' : 'none'}
                          validationMessage={bodyError || null}
                        >
                          <JsonEditor
                            className={styles.jsonEditor}
                            value={body}
                            onChange={(value: string) => {
                              setBody(value);
                              if (value.trim()) {
                                try { JSON.parse(value); setBodyError(''); } catch { setBodyError('Invalid JSON'); }
                              } else {
                                setBodyError('');
                              }
                            }}
                            hasError={!!bodyError}
                            minHeight={150}
                            maxHeight={300}
                          />
                        </Field>
                      </>
                    ) : (
                      // Published non-Request trigger: no payload needed
                      <Text className={styles.noPayloadMessage}>
                        This trigger does not accept a payload. Click Run to trigger the workflow.
                      </Text>
                    )}
                  </div>
                )}

                {/* Missing Connections Error */}
                {invalidConnectionNodeIds.length > 0 && (
                  <MessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                      {'Missing connections for some operations. '}
                      <Link
                        as="button"
                        type="button"
                        onClick={() => setIsConnectionsOpen(true)}
                      >
                        Update Connections
                      </Link>
                    </MessageBarBody>
                  </MessageBar>
                )}

                {/* Validation Warning */}
                {hasValidated && validationErrorCount > 0 && (() => {
                  const visibleErrors = showAllValidationErrors
                    ? validationErrors
                    : validationErrors.slice(0, VALIDATION_ERROR_PREVIEW_LIMIT);
                  const hasOverflow = validationErrorCount > VALIDATION_ERROR_PREVIEW_LIMIT;
                  const errorLabel = validationErrorCount === 1 ? 'error' : 'errors';
                  return (
                    <MessageBar intent="warning" layout="multiline">
                      <MessageBarBody>
                        This workflow has {validationErrorCount} validation {errorLabel} that may
                        cause the run to fail.
                        <ul className={styles.validationErrorList}>
                          {visibleErrors.map((item, i) => (
                            <li
                              key={`${item.nodeName ?? 'global'}-${i}`}
                              className={styles.validationErrorItem}
                            >
                              {item.nodeName && (
                                <span className={styles.validationErrorNode}>
                                  {item.nodeName}:{' '}
                                </span>
                              )}
                              {item.error.message}
                            </li>
                          ))}
                        </ul>
                        {hasOverflow && (
                          <Button
                            className={styles.validationErrorToggle}
                            appearance="transparent"
                            size="small"
                            onClick={() => setShowAllValidationErrors((v) => !v)}
                          >
                            {showAllValidationErrors
                              ? 'Show fewer'
                              : `Show all ${validationErrorCount} ${errorLabel}`}
                          </Button>
                        )}
                      </MessageBarBody>
                    </MessageBar>
                  );
                })()}

                {/* Revision Preparing Notice */}
                {isPreparingRevision && (
                  <MessageBar
                    intent="info"
                    icon={<Spinner size="extra-tiny" />}
                    data-testid="trigger-dialog-preparing"
                  >
                    <MessageBarBody>
                      Preparing your workflow for a test run. This usually takes a few seconds...
                    </MessageBarBody>
                  </MessageBar>
                )}

                {/* Trigger Error */}
                {triggerError && (
                  <div className={styles.errorBar}>
                    <ErrorCircleRegular className={styles.errorIcon} />
                    <span>{triggerError}</span>
                  </div>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={
                isSavingDraft || triggerMutation.isPending || isRunningTrigger || isPreparingRevision
                  ? <Spinner size="tiny" />
                  : <PlayFilled />
              }
              onClick={handleTrigger}
              disabled={!canTrigger || invalidConnectionNodeIds.length > 0 || isSavingDraft || isRunningTrigger || triggerMutation.isPending || isPreparingRevision}
            >
              {getSubmitButtonLabel({ isSavingDraft, isPending: triggerMutation.isPending || isRunningTrigger, isPreparingRevision, isDraft: isDraftMode, isMock: useMockRun })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    {isConnectionsOpen && (
      <ConnectionsDialog
        open={isConnectionsOpen}
        onClose={() => setIsConnectionsOpen(false)}
        nodeIds={invalidConnectionNodeIds}
      />
    )}
    </>
  );
};
